import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';

const Solution = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const scrollToDemo = () => {
    const demoSection = document.getElementById('demo');
    if (demoSection) {
      const offset = 60; // Stop 60px higher
      const elementPosition = demoSection.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="pt-8 bg-white relative overflow-hidden" id="solution" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-8">
        <div className={`text-center mb-16 transition-all duration-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-8 font-sans">
            Learn to <span className="text-green-500 font-black">Spot Scams</span> Before They Happen
          </h2>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold font-sans">AI-Driven Training</CardTitle>
                <Separator />
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-600 font-sans leading-relaxed">Our advanced AI training simulations help your elderly loved ones recognize scam patterns in a safe, controlled environment. Every training call is transparent about its educational nature, ensuring you understand you're practicing with AI while experiencing realistic scenarios that build real-world scam detection skills.</CardDescription>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold font-sans">Quick Simulations</CardTitle>
                <Separator />
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-600 font-sans leading-relaxed">Each training session is carefully designed to be brief and manageable, lasting just 2-3 minutes to prevent fatigue and maintain engagement. This focused approach ensures your loved ones can practice scam detection skills without feeling overwhelmed, making learning comfortable and effective while building confidence through repeated, short real world scenarios.</CardDescription>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold font-sans">Ethical Approach</CardTitle>
                <Separator />
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-600 font-sans leading-relaxed">We believe in building skills through positive reinforcement and encouragement, never through fear or intimidation. Our training approach respects the dignity and comfort of every participant, creating a supportive learning environment where your loved ones can develop scam detection skills with confidence and peace of mind.</CardDescription>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-semibold font-sans">Build Reflexes</CardTitle>
                <Separator />
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-600 font-sans leading-relaxed">Through repeated practice, your loved ones develop instinctive responses that become second nature when encountering suspicious calls. These automatic reflexes help them confidently disengage from potential scams, verify information independently, and know when to seek help from trusted family members or authorities.</CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solution;
